import { AntDesign, Feather, Ionicons } from "@expo/vector-icons";
import { Stack, Text, VStack } from "@towersystems/roam-common-native";
import { Pressable } from "react-native";
import { Colors, Spacings, View } from "react-native-ui-lib";
import Product from "../../../../../../utilities/data/model/Product";
import StockCountItem from "../../../../../../utilities/data/model/StockCountItem";
import StockCountLine from "../../../../../../utilities/data/model/StockCountLine";
import {
  useCurrentCountContext,
  useStockCountContext,
  useStockCountItemSyncContext,
} from "../../../../../../utilities/stock-count/hooks";
import { formatDateToCalendar } from "@towersystems/roam-common/lib/shared-utils";
import React from "react";
import { StockCountSource } from "@towersystems/roam-common/lib/generated-types";

export interface StockCountHistoryListItemProps {
  stockCountItem: StockCountItem;
  product?: Product;
  stockCountLine: StockCountLine;
  animationEnabled?: boolean;
}

export function StockCountHistoryListItem({
  stockCountItem,
}: StockCountHistoryListItemProps) {
  const { processAndSyncCountById } = useStockCountItemSyncContext();
  const { handleExportStockCountItems } = useStockCountContext();

  const [syncFailed, setSyncFailed] = React.useState(false);
  const [exportFailed, setExportFailed] = React.useState(false);

  const [isSyncing, setIsSyncing] = React.useState(false);
  const [isExportErrorHidden, setIsExportErrorHidden] = React.useState(false);

  const { stockCount } = useCurrentCountContext();
  const isInternalCount = stockCount.source == StockCountSource.INTERNAL;

  function handleSync() {
    setIsSyncing(true);
    processAndSyncCountById(stockCountItem.id).finally(() =>
      setIsSyncing(false)
    );
  }

  function handleExport() {
    setIsExportErrorHidden(true);
    if (!stockCountItem.remoteId) return;
    handleExportStockCountItems([stockCountItem.remoteId]);
  }

  const showSyncFailedNotification = syncFailed && !isSyncing;
  const showExportFailedNotification = exportFailed && !isExportErrorHidden;

  let rightMarkup = undefined;
  let retryAction = undefined;

  if (false === isInternalCount) {
    if (showSyncFailedNotification) {
      const syncFailedLabel = `${stockCountItem.numSyncAttempts} sync attempts failed`;
      rightMarkup = <HistoryErrorMarkup label={syncFailedLabel} />;
      retryAction = handleSync;
    } else if (showExportFailedNotification) {
      const exportFailedLabel = `Export failed`;
      rightMarkup = <HistoryErrorMarkup label={exportFailedLabel} />;
      retryAction = handleExport;
    }
  } else {
    if (showSyncFailedNotification) {
      rightMarkup = (
        <View style={{ marginRight: Spacings.s5 }}>
          <Text variant="heading1">
            <Ionicons name="warning" size={18} color={Colors.destructive} />
          </Text>
        </View>
      );
    } else {
      rightMarkup = (
        <View style={{ marginRight: Spacings.s5 }}>
          <Text variant="heading1">
            <Feather name="check" size={18} color={Colors.success} />
          </Text>
        </View>
      );
    }
  }

  React.useEffect(() => {
    const subscription = stockCountItem
      .observe()
      .subscribe((newStockCountItem) => {
        const hasSyncFailed = newStockCountItem.hasSyncFailed();

        setSyncFailed(hasSyncFailed);

        newStockCountItem
          .hasExportFailed()
          .then((newExportFailedState) => {
            setIsExportErrorHidden(false);
            setExportFailed(!!newExportFailedState);
          })
          .catch(console.log);
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [stockCountItem]);

  return (
    <Wrapper onPress={retryAction}>
      <View
        flex
        row
        style={{
          justifyContent: "space-between",
          maxWidth: "100%",
          overflow: "hidden",
          paddingVertical: Spacings.s5,
          alignItems: "center",
        }}
      >
        <View
          style={{
            overflow: "hidden",
            flex: 1,
          }}
        >
          <View
            flex
            style={{ overflow: "hidden", paddingRight: Spacings.s4 * 2 }}
          >
            <View row centerV>
              <Stack vertical spacing={1}>
                <Text>{stockCountItem.counted} counted</Text>
                {stockCountItem.countedAt ? (
                  <Text color="subdued">
                    {formatDateToCalendar(
                      stockCountItem.countedAt.toISOString()
                    )}
                  </Text>
                ) : undefined}
              </Stack>

              <View style={{ flexShrink: 1 }}></View>
            </View>
          </View>
        </View>
        <View row style={{ alignItems: "center", flexShrink: 0 }}>
          {rightMarkup}
        </View>
      </View>
    </Wrapper>
  );
}

type WrapperProps = {
  onPress?: () => void;
  children?: React.ReactNode;
};

function Wrapper({ onPress, children }: WrapperProps) {
  if (onPress) {
    return (
      <Pressable onPress={onPress} style={{ justifyContent: "center" }}>
        {children}
      </Pressable>
    );
  }

  return <View flex>{children}</View>;
}

type HistoryErrorProps = {
  label: string;
};

const HistoryErrorMarkup = ({ label }: HistoryErrorProps) => (
  <VStack centerHorizontal centerVertical spacing={0}>
    <AntDesign name="exclamationcircleo" size={24} color={Colors.destructive} />
    <Text color="destructive">{label}</Text>
  </VStack>
);
